import { useEffect, useState } from "react";
import { ShieldOff, Plus, Trash2, Search, Shield, Info } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";

const REASON_LABELS = {
  bounce_hard: { label: "Bounce קשה", cls: "bg-red-50 text-red-700 border-red-200" },
  bounce_soft: { label: "Bounce רך", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  complaint:   { label: "תלונת ספאם", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  manual:      { label: "ידני", cls: "bg-stone-100 text-stone-700 border-stone-200" },
  lead_deleted:{ label: "ליד נמחק", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function AdminSuppressions() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [checkEmail, setCheckEmail] = useState("");
  const [checkResult, setCheckResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get("/admin/suppressions"),
        api.get("/admin/email/webhook-events?limit=50"),
      ]);
      setItems(s.data);
      setEvents(e.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addManual = async () => {
    if (!newEmail.trim()) return toast.error("אימייל חובה");
    try {
      await api.post("/admin/suppressions", { email: newEmail.trim(), reason: "manual" });
      toast.success("נוסף להשתקה");
      setNewEmail("");
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה");
    }
  };

  const check = async () => {
    if (!checkEmail.trim()) return;
    try {
      const { data } = await api.post("/admin/suppressions/check", { email: checkEmail.trim() });
      setCheckResult(data);
    } catch (err) {
      toast.error("שגיאה בבדיקה");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("להסיר מרשימת ההשתקה? הליד לא יסומן מחדש כמנוי — יש לעשות זאת ידנית בעריכת הליד.")) return;
    try {
      await api.delete(`/admin/suppressions/${id}`);
      toast.success("הוסר");
      load();
    } catch (err) {
      toast.error("שגיאה");
    }
  };

  return (
    <AdminLayout title="השתקות · Webhooks">
      <div className="flex items-center gap-2 mb-5 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
        <Info className="w-5 h-5 text-brand-amber flex-shrink-0" />
        <p className="text-sm text-stone-700">
          כתובות אימייל שמורות כ-<strong>SHA-256 hash</strong> בלבד (פרטיות/GDPR). לא ניתן לאחזר את הכתובת המקורית.
          כתובות מושתקות לא יקבלו מיילים אפילו אם ירשמו שוב.
        </p>
      </div>

      <Tabs defaultValue="list" className="w-full">
        <TabsList className="mb-4" data-testid="suppressions-tabs">
          <TabsTrigger value="list" data-testid="tab-list"><ShieldOff className="w-4 h-4 me-2" />רשימת השתקות ({items.length})</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Webhook Events ({events.length})</TabsTrigger>
          <TabsTrigger value="check" data-testid="tab-check">בדיקת כתובת</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <div className="flex items-center justify-end mb-4">
            <Button onClick={() => setCreating(true)} data-testid="new-suppression-btn" className="h-11 rounded-full bg-brand-charcoal hover:bg-stone-800 text-white">
              <Plus className="w-4 h-4 me-2" />הוסף השתקה ידנית
            </Button>
          </div>

          {loading ? (
            <div className="py-12 text-center text-stone-500">טוען...</div>
          ) : items.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="suppressions-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200 text-right">
                  <tr>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">תאריך</th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">סיבה</th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">מקור</th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Hash</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const badge = REASON_LABELS[s.reason] || REASON_LABELS.manual;
                    return (
                      <tr key={s.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`sup-${s.id}`}>
                        <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">
                          {new Date(s.created_at).toLocaleString("he-IL")}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-stone-600">{s.source || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-stone-400 truncate max-w-[280px]" title={s.email_hash}>
                          {s.email_hash.substring(0, 24)}…
                        </td>
                        <td className="px-4 py-3 text-end">
                          <button onClick={() => remove(s.id)} data-testid={`sup-remove-${s.id}`} className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600" title="הסר מהרשימה">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="events">
          {events.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 border border-dashed border-stone-300 text-center">
              <p className="text-sm text-stone-500">אין אירועי webhook עדיין. הגדר webhook ב-Resend Dashboard → נתיב: <code className="ltr bg-stone-100 rounded px-2 py-1">/api/webhooks/resend</code></p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="events-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200 text-right">
                  <tr>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">תאריך</th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Type</th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Recipient</th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Resend ID</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => (
                    <tr key={e.id} className="border-b border-stone-100" data-testid={`event-${e.id}`}>
                      <td className="px-4 py-3 text-xs text-stone-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString("he-IL")}</td>
                      <td className="px-4 py-3"><code className="ltr text-xs bg-stone-100 px-2 py-0.5 rounded">{e.type}</code></td>
                      <td className="px-4 py-3 ltr text-left text-xs">{e.recipient || "—"}</td>
                      <td className="px-4 py-3 ltr text-left text-xs text-stone-400 truncate max-w-[220px]" title={e.resend_email_id}>{e.resend_email_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="check">
          <div className="bg-white rounded-2xl p-8 border border-stone-200 max-w-lg mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Search className="w-5 h-5 text-brand-amber" />
              <h3 className="font-[800]">בדיקה: האם כתובת אימייל מושתקת?</h3>
            </div>
            <p className="text-xs text-stone-500 mb-4">הכתובת נשלחת לשרת, נהשית ל-SHA-256, ונבדקת מול הרשימה. הכתובת המקורית לא נשמרת.</p>
            <div className="flex gap-2">
              <Input value={checkEmail} onChange={(e) => setCheckEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && check()} placeholder="user@example.com" className="h-11 ltr" data-testid="check-email-input" />
              <Button onClick={check} className="h-11 bg-brand-charcoal text-white rounded-full" data-testid="check-btn">בדוק</Button>
            </div>
            {checkResult && (
              <div className={`mt-4 rounded-xl px-4 py-3 border ${checkResult.suppressed ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="check-result">
                <div className="flex items-center gap-2">
                  {checkResult.suppressed ? (
                    <>
                      <ShieldOff className="w-5 h-5 text-red-600" />
                      <span className="font-bold text-red-800">מושתק</span>
                      <span className="text-sm text-red-700">— לא יקבל מיילים</span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5 text-emerald-600" />
                      <span className="font-bold text-emerald-800">לא מושתק</span>
                      <span className="text-sm text-emerald-700">— יכול לקבל מיילים</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Manual Add Dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent dir="rtl" className="max-w-md">
          <DialogHeader>
            <DialogTitle>הוסף כתובת להשתקה</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-sm font-bold">אימייל</Label>
            <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="user@example.com" className="h-11 ltr" data-testid="manual-suppress-input" />
            <p className="text-xs text-stone-500">
              הכתובת תיהשה ל-SHA-256. כל ליד קיים עם הכתובת הזו יסומן כ-unsubscribed אוטומטית.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>ביטול</Button>
            <Button onClick={addManual} data-testid="manual-suppress-save" className="bg-brand-charcoal text-white">השתק</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-2xl p-12 border border-dashed border-stone-300 text-center" data-testid="suppressions-empty">
      <ShieldOff className="w-12 h-12 text-stone-300 mx-auto mb-4" />
      <h3 className="font-[800] mb-2">אין כתובות מושתקות</h3>
      <p className="text-sm text-stone-500 max-w-md mx-auto">
        כתובות שהחזירו bounce קשה או תלונת ספאם יתווספו כאן אוטומטית מ-Resend webhooks.
      </p>
    </div>
  );
}
